/**
 * chordCandidates.ts
 *
 * Supplies chordRespell's CandidateProvider by re-analysing a chart entry's
 * voicing, with a cache so a chart full of repeated voicings costs one request
 * per *distinct* voicing rather than one per chord.
 *
 * WHY A LOOKUP IS NEEDED AT ALL
 * -----------------------------
 * Chart entries store only the chosen segment plus the voicing (see
 * addEntryToChart in App.tsx) — the alternate spellings shown in the Chord
 * Symbol panel are transient state for the chord currently being played. So to
 * offer alternates for a chord already sitting in a chart, its voicing has to
 * go back through the analysis server.
 *
 * One response carries both orderings (`cs_options_segments` = FEEL,
 * `cs_options_segments_notation` = READ, sorted by legibility_score in
 * get_cs.py), so a single request serves both respell orderings and the
 * iRealPro respelling.
 */

import axios from 'axios';
import { ChordChartEntry, ChordSegment } from './chordChartExport';
import { CandidateProvider, ChordCandidates, NO_CANDIDATES } from './chordRespell';

export type CandidateProviderOptions = {
  /** Analysis server URL — the same `server` constant App.tsx posts to. */
  server: string;
  /** Locked key centre pitch class, or -1 for none. */
  keyCenter: number;
  /** Locked key mode (0 major / 1 minor). */
  keyMode: number;
  /**
   * The app's current `settings.preferNotationLegible`.
   *
   * This is NOT just a sort order: get_cs.py feeds notation_legible into chord
   * naming itself, so it can change which segments come back, not merely their
   * ranking. Pass the user's current setting so the alternates offered here are
   * the same ones the Chord Symbol panel would show — anything else would
   * suggest respellings the user cannot reproduce in the panel.
   */
  notationLegible: boolean;
  /** The app's current `settings.preferModeBorrowing`. */
  preferModeBorrowing: boolean;
  /**
   * Optional shared cache. Pass one to reuse results across operations (e.g. an
   * iRealPro respell followed by an ordering respell). Keys include the analysis
   * settings, so sharing is safe even if those change between operations.
   */
  cache?: CandidateCache;
};

/**
 * Cache of in-flight and completed lookups.
 *
 * Values are Promises, not resolved values, so two chords with the same voicing
 * requested before the first response lands share one request instead of racing
 * two. A chart of 32 chords over 5 distinct voicings makes 5 requests.
 */
export type CandidateCache = Map<string, Promise<ChordCandidates>>;

/** Creates an empty cache the caller can hold across operations. */
export function createCandidateCache(): CandidateCache {
  return new Map();
}

// ---------------------------------------------------------------------------
// Requesting, with retries
// ---------------------------------------------------------------------------

/**
 * WHY RETRIES
 * -----------
 * A batch respell fires one request per distinct voicing back-to-back, which is
 * a burst nothing else in the app produces — playing chords sends one request
 * per (debounced) voicing change. Two things break under a burst that never
 * break while playing:
 *
 *   - the server's rate limit (30/second on /sentiserver, see sentiserver.py),
 *     which a 30-chord chart reaches on its own;
 *   - ordinary transient network failures, which now cost a whole chord's
 *     suggestions instead of one frame of chord display.
 *
 * Both are momentary, so retrying a couple of times with a short backoff turns
 * a reported "failure" back into a result. Only failures that could plausibly
 * succeed on a retry are retried — a 400 means the request itself is wrong and
 * would fail identically forever.
 */
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 250;
/**
 * Per-request ceiling. axios defaults to no timeout at all, so without this one
 * stalled connection stalls the whole batch indefinitely — the operation would
 * simply never return and the dialog would never open.
 */
const REQUEST_TIMEOUT_MS = 20000;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** True when trying the same request again could plausibly succeed. */
function isRetryable(err: any): boolean {
  const response = err && err.response;
  // No response at all: network error, timeout, connection reset — all transient.
  if (!response) return true;
  const status = response.status;
  return status === 429 || status >= 500;
}

/** How long to wait before the next attempt, honouring Retry-After when sent. */
function retryDelayMs(err: any, attempt: number): number {
  const response = err && err.response;
  const headers = response && response.headers;
  const retryAfter = headers ? headers['retry-after'] : null;
  const seconds = retryAfter ? parseFloat(retryAfter) : NaN;
  if (!isNaN(seconds) && seconds >= 0) return Math.min(seconds * 1000, 5000);
  return RETRY_BASE_DELAY_MS * Math.pow(2, attempt); // 250ms, 500ms
}

export async function postWithRetry(server: string, body: object) {
  let lastError: any = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await axios.post(server, body, { timeout: REQUEST_TIMEOUT_MS });
    } catch (err) {
      lastError = err;
      if (!isRetryable(err) || attempt === MAX_ATTEMPTS - 1) throw err;
      await delay(retryDelayMs(err, attempt));
    }
  }
  throw lastError;
}

/**
 * The notes to analyse for a stored voicing: distinct, sorted.
 *
 * Chart entries store `[...activeNotes, ...heldNotes, ...toggledNotes]`
 * (addEntryToChart in App.tsx) WITHOUT deduping, so the same note appears two
 * or three times whenever it is held and toggled at once — which is the normal
 * state after selecting a chart chord, and after any sustained passage. The
 * live analysis path dedupes before posting ([...new Set(...)] in App.tsx), so
 * deduping here is not a tweak: it is what makes a re-analysis ask the same
 * question the chord was originally named from.
 *
 * Two concrete failures it prevents:
 *   - a 400 from the server, whose MAX_NOTES=32 limit counts duplicates, so a
 *     dense sustained voicing is rejected at three copies of ~11 notes;
 *   - silently different candidates, since duplicate notes feed
 *     get_tonal_gravity's pitch-class weights and can move the root.
 */
function analysisNotes(voicing: number[]): number[] {
  return Array.from(new Set(voicing)).sort((a, b) => a - b);
}

/**
 * Cache key for one lookup. Includes every input that can change the result,
 * so a shared cache never returns candidates computed under other settings.
 */
function cacheKey(voicing: number[], options: CandidateProviderOptions): string {
  const notes = analysisNotes(voicing).join(',');
  return [
    notes,
    options.keyCenter,
    options.keyMode,
    options.notationLegible ? 1 : 0,
    options.preferModeBorrowing ? 1 : 0,
  ].join('|');
}

/**
 * Builds a CandidateProvider bound to the given analysis settings.
 *
 * Rejections propagate to the caller — resolveChart/respellChartByOrdering catch them per
 * chord, count them in `lookupFailures`, and carry on. Do not swallow errors
 * here, or a dead backend would look like "no alternates available" and the
 * user would never learn the result was incomplete. What does reach the caller
 * has already survived MAX_ATTEMPTS tries (see postWithRetry), so a reported
 * failure is a persistent one, not a blip in the middle of a burst.
 */
export function createCandidateProvider(
  options: CandidateProviderOptions,
): CandidateProvider {
  const cache = options.cache || createCandidateCache();

  return (entry) => {
    const voicing = entry.voicing;
    // A chord with no stored voicing cannot be re-analysed. Not an error —
    // it simply has no alternates.
    if (!voicing || voicing.length === 0) return Promise.resolve(NO_CANDIDATES);

    const key = cacheKey(voicing, options);
    const cached = cache.get(key);
    if (cached) return cached;

    const request = postWithRetry(options.server, {
      notes: analysisNotes(voicing),
      keyCenter: options.keyCenter,
      keyMode: options.keyMode,
      notationLegible: options.notationLegible,
      preferModeBorrowing: options.preferModeBorrowing,
    }).then(response => {
      const data = response.data;

      // Scale responses carry no chord options at all.
      if (!data || data.is_scale) return NO_CANDIDATES;

      const read: ChordSegment[] = data.cs_options_segments_notation || [];
      const feel: ChordSegment[] = data.cs_options_segments || [];
      return { read, feel };
    });

    // Drop failures from the cache so a later retry can succeed rather than
    // replaying the same rejection for the rest of the session.
    request.catch(() => { cache.delete(key); });

    cache.set(key, request);
    return request;
  };
}

// ---------------------------------------------------------------------------
// Batch prefetch
// ---------------------------------------------------------------------------

/**
 * Path appended to `server` for the batch endpoint (analyze_batch in
 * sentiserver.py). `server` is the /sentiserver URL, so the batch route sits
 * directly under it.
 */
const BATCH_PATH = '/batch';

/** Server-side MAX_BATCH_CHORDS. Longer charts go in several requests. */
const MAX_BATCH_CHORDS = 128;

/** One chord's slot in a batch response: options, or a per-chord error. */
type BatchResult = {
  is_scale?: boolean;
  cs_options_segments?: ChordSegment[];
  cs_options_segments_notation?: ChordSegment[];
  error?: string;
};

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

/**
 * Fetches candidates for a whole chart in one request and fills the cache, so
 * the per-chord provider then serves every chord without touching the network.
 *
 * WHY: a chart of 30 distinct voicings previously cost 30 sequential
 * round-trips — about 4.5s against Cloud Run, and enough requests to reach
 * /sentiserver's 30-per-second rate limit. The same 30 chords take one request.
 *
 * BEST-EFFORT BY DESIGN: this is an optimisation, never a correctness
 * requirement. Any failure — an older backend with no /batch route, a network
 * error, a malformed response — simply leaves the cache unfilled, and the
 * per-chord path runs exactly as before, errors and all. That is also what
 * makes it safe to ship the frontend against a backend that predates the
 * endpoint. Nothing here throws.
 *
 * Chords whose slot came back as an error are deliberately NOT cached: letting
 * the per-chord path re-request them means the user still gets the server's
 * real complaint about that voicing instead of a silent "no alternates".
 *
 * Pass the same `options` (including `cache`) used for createCandidateProvider.
 */
export async function prefetchCandidates(
  entries: ChordChartEntry[],
  options: CandidateProviderOptions,
): Promise<void> {
  const cache = options.cache;
  if (!cache) return; // Nothing to prefetch into.

  // Distinct, uncached voicings only: charts repeat voicings, and a second
  // operation on the same chart should cost nothing at all.
  const wanted = new Map<string, number[]>();
  for (const entry of entries) {
    const voicing = entry.voicing;
    if (entry.isSeparator || !voicing || voicing.length === 0) continue;
    const key = cacheKey(voicing, options);
    if (cache.has(key) || wanted.has(key)) continue;
    wanted.set(key, analysisNotes(voicing));
  }
  if (wanted.size === 0) return;

  const keys = Array.from(wanted.keys());
  const url = options.server + BATCH_PATH;

  for (const keyChunk of chunk(keys, MAX_BATCH_CHORDS)) {
    try {
      const response = await postWithRetry(url, {
        chords: keyChunk.map(key => ({ notes: wanted.get(key) })),
        keyCenter: options.keyCenter,
        notationLegible: options.notationLegible,
        // keyMode and preferModeBorrowing are omitted on purpose: they only
        // feed the chord-scale analysis, which cannot change a chord's name
        // options. They stay in the cache key regardless, so a cached entry is
        // never reused under different settings.
      });

      const results: BatchResult[] = (response.data && response.data.results) || [];
      // A length mismatch means we cannot tell which result belongs to which
      // chord. Caching them by position would attach the wrong spellings to
      // real chords, so drop the chunk and let the per-chord path redo it.
      if (results.length !== keyChunk.length) continue;

      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (!result || result.error) continue;
        if (result.is_scale) {
          cache.set(keyChunk[i], Promise.resolve(NO_CANDIDATES));
          continue;
        }
        cache.set(keyChunk[i], Promise.resolve({
          read: result.cs_options_segments_notation || [],
          feel: result.cs_options_segments || [],
        }));
      }
    } catch (err) {
      // Expected against a backend without the endpoint (404). Logged, not
      // thrown: the per-chord path is about to do this work anyway.
      console.warn('[chordCandidates] batch prefetch unavailable, falling back to per-chord lookups', err);
      return;
    }
  }
}
